
import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { RefreshCw, Download, Upload, Globe, Search, ChevronLeft, ChevronRight, Edit, Trash2, Eye, CalendarIcon, Settings, Mail, Cloud } from 'lucide-react'
import { useDomains, useUpdateDomain, useDeleteDomain } from '@/hooks/useDomains'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { format, parse } from 'date-fns'
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { DomainMetrics } from './DomainMetrics'
import { DomainUploadDialog } from './DomainUploadDialog'
import { DomainBulkEditDialog } from './DomainBulkEditDialog'
import { DomainAddDialog } from './DomainAddDialog'
import { DomainColumnManagerDialog } from './DomainColumnManagerDialog'
import { EditableDropdown } from '@/components/ui/editable-dropdown'
import { AdvancedFilter, FilterRule, FieldConfig } from '@/components/ui/advanced-filter'
import { AdvancedFilterBuilder, FilterField, FilterCondition, FilterGroup } from '@/components/ui/advanced-filter-builder'
import { ViewSelector } from './ViewSelector'
import { useViewSettings } from '@/hooks/useViewSettings'
import { TableColumnContextMenu } from '@/components/ui/table-column-context-menu'
import Papa from 'papaparse'

interface DomainTableProps {
  filteredDomains?: any[]
  isLoading?: boolean
  onUpdate?: () => void
  searchQuery?: string
  onSearchChange?: (query: string) => void
  filters?: FilterRule[]
  onFiltersChange?: (filters: FilterRule[]) => void
  filterFields?: FieldConfig[]
}

export function DomainTable({ 
  filteredDomains: propFilteredDomains, 
  isLoading: propIsLoading, 
  onUpdate: propOnUpdate,
  searchQuery = '',
  onSearchChange,
  filters = [],
  onFiltersChange,
  filterFields = []
}: DomainTableProps = {}) {
  const { data: domains = [], isLoading: hookIsLoading, refetch } = useDomains()
  const updateDomain = useUpdateDomain()
  const deleteDomain = useDeleteDomain()
  const { updateView, currentView, currentViewId } = useViewSettings()
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [showAddDomainDialog, setShowAddDomainDialog] = useState(false)
  const [isSyncingSpaceship, setIsSyncingSpaceship] = useState(false)
  const [isSyncingInboxKit, setIsSyncingInboxKit] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [clientFilter, setClientFilter] = useState('all')
  const [selectedDomains, setSelectedDomains] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [editingDomain, setEditingDomain] = useState<any>(null)
  const [showSingleEditDialog, setShowSingleEditDialog] = useState(false)
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false)
  const [showColumnManager, setShowColumnManager] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'domain', 'client_name', 'status', 'domain_registrar', 'mailbox_provider', 'email_provider',
    'total_mailboxes', 'total_emails_sent', 'total_replies', 'total_bounces', 'purchase_date',
    'price', 'auto_renewal', 'expiry_date', 'notes'
  ])
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([])
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({
    'domain': 'Domain',
    'client_name': 'Client',
    'status': 'Status',
    'domain_registrar': 'Domain Registrar',
    'mailbox_provider': 'Mailbox Provider',
    'email_provider': 'Email Provider',
    'total_mailboxes': 'Mailboxes',
    'total_emails_sent': 'Emails Sent',
    'total_replies': 'Replies',
    'total_bounces': 'Bounces',
    'reply_rate': 'Reply %',
    'bounce_rate': 'Bounce %',
    'purchase_date': 'Purchase Date',
    'price': 'Price',
    'auto_renewal': 'Auto Renewal',
    'renewal_date': 'Renewal Date',
    'expiry_date': 'Expiry Date',
    'notes': 'Notes'
  })

  // Use props if provided, otherwise use hook data
  const actualDomains = propFilteredDomains || domains
  const actualIsLoading = propIsLoading !== undefined ? propIsLoading : hookIsLoading
  const actualRefetch = propOnUpdate || refetch

  // State for managing dropdown options that can be dynamically updated
  const [statusOptionsList, setStatusOptionsList] = useState<string[]>([])
  const [registrarOptionsList, setRegistrarOptionsList] = useState<string[]>([])
  const [mailboxProviderOptionsList, setMailboxProviderOptionsList] = useState<string[]>([])
  const [emailProviderOptionsList, setEmailProviderOptionsList] = useState<string[]>([])
  const [advancedFilters, setAdvancedFilters] = useState<FilterCondition[]>([])
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([])
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null)

  // Define filter fields for the advanced filter builder
  const domainFilterFields: FilterField[] = [
    { id: 'domain', label: 'Domain', type: 'text' },
    { id: 'status', label: 'Status', type: 'select', options: statusOptionsList },
    { id: 'domain_registrar', label: 'Domain Registrar', type: 'select', options: registrarOptionsList },
    { id: 'mailbox_provider', label: 'Mailbox Provider', type: 'select', options: mailboxProviderOptionsList },
    { id: 'email_provider', label: 'Email Provider', type: 'select', options: emailProviderOptionsList },
    { id: 'client_name', label: 'Client Name', type: 'select', options: [...new Set(domains.map(d => d.client_name).filter(Boolean))].sort() },
    { id: 'total_emails_sent', label: 'Emails Sent', type: 'number' },
    { id: 'total_replies', label: 'Replies', type: 'number' },
    { id: 'total_bounces', label: 'Bounces', type: 'number' },
    { id: 'reply_rate', label: 'Reply %', type: 'number' },
    { id: 'bounce_rate', label: 'Bounce %', type: 'number' },
    { id: 'purchase_date', label: 'Purchase Date', type: 'date' },
    { id: 'renewal_date', label: 'Renewal Date', type: 'date' },
    { id: 'expiry_date', label: 'Expiry Date', type: 'date' },
    { id: 'auto_renewal', label: 'Auto Renewal', type: 'boolean' },
    { id: 'notes', label: 'Notes', type: 'text' }
  ]

  // Dropdown options based on existing data with fallbacks
  const getUniqueValues = (field: string) => {
    const values = actualDomains
      .map(domain => domain[field])
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort()
    return values
  }

  // Initialize options on component mount or when domains change
  React.useEffect(() => {
    const uniqueStatusValues = getUniqueValues('status')
    const defaultStatusOptions = ['Active', 'Inactive', 'Expired', 'Suspended', 'Deactivated']
    const initStatusOptions = uniqueStatusValues.length > 0 ? 
      [...new Set([...uniqueStatusValues, ...defaultStatusOptions])].sort() : 
      defaultStatusOptions
    
    const initRegistrarOptions = getUniqueValues('domain_registrar').length > 0 ? 
      getUniqueValues('domain_registrar') : 
      ['Hosting.de J-account', 'Hosting.de K-account', 'Hosting.de', 'Cloudflare', 'GoDaddy', 'Namecheap', 'Spaceship', 'InboxKit']
    
    const initMailboxProviderOptions = getUniqueValues('mailbox_provider').length > 0 ? 
      getUniqueValues('mailbox_provider') : 
      ['Superwave', 'Maildoso', 'Cheap Inboxes', 'Primary', 'Google Workspace', 'Microsoft 365', 'InboxKit']
    
    const initEmailProviderOptions = getUniqueValues('email_provider').length > 0 ? 
      getUniqueValues('email_provider') : 
      ['Google', 'Microsoft', 'Cloudflare']

    setStatusOptionsList(initStatusOptions)
    setRegistrarOptionsList(initRegistrarOptions)
    setMailboxProviderOptionsList(initMailboxProviderOptions)
    setEmailProviderOptionsList(initEmailProviderOptions)
  }, [actualDomains])

  // Apply advanced filters to domains
  const applyAdvancedFilters = (domains: any[], conditions: FilterCondition[], groups: FilterGroup[]) => {
    if (conditions.length === 0 && groups.length === 0) return domains

    return domains.filter(domain => {
      // Apply top-level conditions (all must be true - AND logic)
      const topLevelConditions = conditions.filter(c => !c.groupId)
      const topLevelMatch = topLevelConditions.every(condition => {
        return evaluateCondition(domain, condition)
      })

      // Apply group conditions (each group is OR logic internally, AND between groups)
      const groupMatches = groups.map(group => {
        const groupConditions = conditions.filter(c => c.groupId === group.id)
        if (group.operator === 'OR') {
          return groupConditions.some(condition => evaluateCondition(domain, condition))
        } else {
          return groupConditions.every(condition => evaluateCondition(domain, condition))
        }
      })

      return topLevelMatch && groupMatches.every(match => match)
    })
  }

  const evaluateCondition = (domain: any, condition: FilterCondition): boolean => {
    const value = domain[condition.field]
    const conditionValue = condition.value

    switch (condition.operator) {
      case 'equals':
        return String(value).toLowerCase() === String(conditionValue).toLowerCase()
      case 'not_equals':
        return String(value).toLowerCase() !== String(conditionValue).toLowerCase()
      case 'contains':
        return String(value).toLowerCase().includes(String(conditionValue).toLowerCase())
      case 'not_contains':
        return !String(value).toLowerCase().includes(String(conditionValue).toLowerCase())
      case 'starts_with':
        return String(value).toLowerCase().startsWith(String(conditionValue).toLowerCase())
      case 'ends_with':
        return String(value).toLowerCase().endsWith(String(conditionValue).toLowerCase())
      case 'is_empty':
        return !value || value === ''
      case 'is_not_empty':
        return !!value && value !== ''
      case 'greater_than':
        return Number(value) > Number(conditionValue)
      case 'greater_than_or_equal':
        return Number(value) >= Number(conditionValue)
      case 'less_than':
        return Number(value) < Number(conditionValue)
      case 'less_than_or_equal':
        return Number(value) <= Number(conditionValue)
      case 'before':
        return new Date(value) < new Date(conditionValue)
      case 'after':
        return new Date(value) > new Date(conditionValue)
      case 'is_true':
        return Boolean(value) === true
      case 'is_false':
        return Boolean(value) === false
      default:
        return true
    }
  }

  // Load column visibility and order settings
  React.useEffect(() => {
    const loadColumnConfig = () => {
      try {
        const savedConfig = localStorage.getItem('domain-column-config')
        if (savedConfig) {
          const parsedConfig = JSON.parse(savedConfig)
          // Apply both visibility and order from saved config
          const orderedVisibleColumns = parsedConfig
            .filter((col: any) => col.visible)
            .sort((a: any, b: any) => a.order - b.order)
            .map((col: any) => col.id)
          setVisibleColumns(orderedVisibleColumns)
        }
        
        // Load pinned columns
        const pinnedConfig = localStorage.getItem('domain-pinned-columns')
        if (pinnedConfig) {
          setPinnedColumns(JSON.parse(pinnedConfig))
        }
        
        // Load custom column labels
        const labelsConfig = localStorage.getItem('domain-column-labels')
        if (labelsConfig) {
          setColumnLabels(prev => ({ ...prev, ...JSON.parse(labelsConfig) }))
        }
      } catch (error) {
        console.error('Error loading column configuration:', error)
      }
    }
    loadColumnConfig()
  }, [])


  // Helper functions to add new options to the lists
  const addNewOption = (optionType: 'status' | 'registrar' | 'mailbox_provider' | 'email_provider', newValue: string) => {
    switch (optionType) {
      case 'status':
        if (!statusOptionsList.includes(newValue)) {
          setStatusOptionsList(prev => [...prev, newValue].sort())
        }
        break
      case 'registrar':
        if (!registrarOptionsList.includes(newValue)) {
          setRegistrarOptionsList(prev => [...prev, newValue].sort())
        }
        break
      case 'mailbox_provider':
        if (!mailboxProviderOptionsList.includes(newValue)) {
          setMailboxProviderOptionsList(prev => [...prev, newValue].sort())
        }
        break
      case 'email_provider':
        if (!emailProviderOptionsList.includes(newValue)) {
          setEmailProviderOptionsList(prev => [...prev, newValue].sort())
        }
        break
    }
  }

  // Helper function to format dates to DD/MM/YYYY
  const formatDateDisplay = (dateValue: string | null): string => {
    if (!dateValue) return '-'
    try {
      const date = new Date(dateValue)
      if (isNaN(date.getTime())) return dateValue // Return as-is if not a valid date
      return format(date, 'dd/MM/yyyy')
    } catch (error) {
      return dateValue // Return as-is if formatting fails
    }
  }

  // Helper function to format date for input (DD/MM/YYYY)
  const formatDateForInput = (dateValue: string | null): string => {
    if (!dateValue) return ''
    try {
      const date = new Date(dateValue)
      if (isNaN(date.getTime())) return ''
      return format(date, 'dd/MM/yyyy')
    } catch (error) {
      return ''
    }
  }

  // Helper function to convert DD/MM/YYYY to YYYY-MM-DD for database
  const convertDateForDatabase = (dateString: string): string => {
    if (!dateString || dateString === '') return ''
    try {
      const date = parse(dateString, 'dd/MM/yyyy', new Date())
      return format(date, 'yyyy-MM-dd')
    } catch (error) {
      return ''
    }
  }

  const uniqueClients = [...new Set(domains.map(d => d.client_name).filter(Boolean))].sort()

  // Apply both basic filters and advanced filters
  let filteredDomains = propFilteredDomains || domains.filter(domain => {
    const matchesSearch = searchTerm === '' || 
      domain.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (domain.client_name && domain.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
    
    const matchesStatus = statusFilter === 'all' || domain.status === statusFilter
    const matchesClient = clientFilter === 'all' || domain.client_name === clientFilter
    
    return matchesSearch && matchesStatus && matchesClient
  })

  // Apply advanced filters if any are set
  filteredDomains = applyAdvancedFilters(filteredDomains, advancedFilters, filterGroups)

  // Apply sorting if configured, otherwise default to total_emails_sent descending
  filteredDomains = filteredDomains.sort((a, b) => {
    if (sortConfig) {
      const { column, direction } = sortConfig
      let aVal = a[column]
      let bVal = b[column]
      
      // Handle different data types
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase()
        bVal = bVal.toLowerCase()
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        // Numbers are handled as-is
      } else {
        // Convert other types to strings for comparison
        aVal = String(aVal || '').toLowerCase()
        bVal = String(bVal || '').toLowerCase()
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1
      if (aVal > bVal) return direction === 'asc' ? 1 : -1
      return 0
    } else {
      // Default sort: Active domains first, then by total_emails_sent descending
      const statusOrder: Record<string, number> = { 'Active': 0, 'Blacklisted': 1, 'Expired': 2, 'Deactivated': 3, 'Shutdown': 4 }
      const aStatus = statusOrder[a.status || ''] ?? 5
      const bStatus = statusOrder[b.status || ''] ?? 5
      if (aStatus !== bStatus) return aStatus - bStatus
      return (b.total_emails_sent || 0) - (a.total_emails_sent || 0)
    }
  })

  // Pagination calculations
  const totalPages = Math.ceil(filteredDomains.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentDomains = filteredDomains.slice(startIndex, endIndex)

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, clientFilter, filteredDomains.length])

  const handleSelectAll = (checked: boolean | string) => {
    const isChecked = checked === true
    if (isChecked) {
      setSelectedDomains(currentDomains.map(domain => domain.id))
    } else {
      setSelectedDomains([])
    }
  }

  const handleSelectDomain = (domainId: string, checked: boolean | string) => {
    const isChecked = checked === true
    if (isChecked) {
      setSelectedDomains(prev => [...prev, domainId])
    } else {
      setSelectedDomains(prev => prev.filter(id => id !== domainId))
    }
  }

  const isAllSelected = currentDomains.length > 0 && currentDomains.every(domain => selectedDomains.includes(domain.id))
  const isIndeterminate = selectedDomains.length > 0 && !isAllSelected

  // Edit functionality
  const handleEditDomain = (domain: any) => {
    setEditingDomain(domain)
    setShowSingleEditDialog(true)
  }

  const handleExport = () => {
    const dataToExport = filteredDomains.map(domain => {
      return {
        domain: domain.domain,
        status: domain.status,
        domain_registrar: domain.domain_registrar,
        mailbox_provider: domain.mailbox_provider,
        email_provider: domain.email_provider,
        client_name: domain.client_name,
        total_emails_sent: domain.total_emails_sent,
        total_replies: domain.total_email_replies,
        total_bounces: domain.total_email_bounces,
        reply_rate: domain.total_emails_sent > 0 ? `${((domain.total_email_replies / domain.total_emails_sent) * 100).toFixed(1)}%` : '0.0%',
        bounce_rate: domain.total_emails_sent > 0 ? `${((domain.total_email_bounces / domain.total_emails_sent) * 100).toFixed(1)}%` : '0.0%',
        purchase_date: domain.purchase_date,
        price: domain.price ? `$${Number(domain.price).toFixed(2)}` : '',
        auto_renewal: domain.auto_renewal,
        renewal_date: domain.renewal_date,
        expiry_date: domain.expiry_date,
        notes: domain.notes
      }
    })

    const csv = Papa.unparse(dataToExport)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `domains-export-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }


  const handleDeleteDomain = async (domain: any) => {
    const confirmed = window.confirm(`Are you sure you want to delete domain "${domain.domain}"? This action cannot be undone.`)
    
    if (!confirmed) return

    try {
      await deleteDomain.mutateAsync(domain.id)
      toast({
        title: "Success",
        description: `Domain "${domain.domain}" has been deleted successfully`,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete domain",
        variant: "destructive"
      })
    }
  }

  // Column management functions
  const handlePinColumn = (columnId: string) => {
    const newPinned = [...pinnedColumns, columnId]
    setPinnedColumns(newPinned)
    localStorage.setItem('domain-pinned-columns', JSON.stringify(newPinned))
  }

  const handleUnpinColumn = (columnId: string) => {
    const newPinned = pinnedColumns.filter(id => id !== columnId)
    setPinnedColumns(newPinned)
    localStorage.setItem('domain-pinned-columns', JSON.stringify(newPinned))
  }

  const handleHideColumn = (columnId: string) => {
    const newVisible = visibleColumns.filter(id => id !== columnId)
    setVisibleColumns(newVisible)
    
    // Update current view to persist column changes
    updateView(currentViewId, { visibleColumns: newVisible })
    
    // Update localStorage to maintain consistency
    const savedConfig = localStorage.getItem('domain-column-config')
    if (savedConfig) {
      const parsedConfig = JSON.parse(savedConfig)
      const updatedConfig = parsedConfig.map((col: any) => 
        col.id === columnId ? { ...col, visible: false } : col
      )
      localStorage.setItem('domain-column-config', JSON.stringify(updatedConfig))
    }
  }

  const handleShowColumn = (columnId: string) => {
    if (!visibleColumns.includes(columnId)) {
      const newVisible = [...visibleColumns, columnId]
      setVisibleColumns(newVisible)
      
      // Update current view to persist column changes
      updateView(currentViewId, { visibleColumns: newVisible })
      
      // Update localStorage to maintain consistency
      const savedConfig = localStorage.getItem('domain-column-config')
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig)
        const updatedConfig = parsedConfig.map((col: any) => 
          col.id === columnId ? { ...col, visible: true } : col
        )
        localStorage.setItem('domain-column-config', JSON.stringify(updatedConfig))
      }
    }
  }

  const handleRenameColumn = (columnId: string, newLabel: string) => {
    const newLabels = { ...columnLabels, [columnId]: newLabel }
    setColumnLabels(newLabels)
    localStorage.setItem('domain-column-labels', JSON.stringify(newLabels))
  }

  const handleSort = (columnId: string, direction: 'asc' | 'desc') => {
    setSortConfig({ column: columnId, direction })
  }

  const handleFilterColumn = (columnId: string) => {
    // This would trigger a filter dialog for the specific column
    console.log('Filter column:', columnId)
    // TODO: Implement column-specific filtering
  }

  const handleSeeMailboxes = () => {
    if (selectedDomains.length === 0) return
    
    // Get the domain names for the selected domains
    const selectedDomainNames = actualDomains
      .filter(domain => selectedDomains.includes(domain.id))
      .map(domain => domain.domain)
    
    // Navigate to mailbox tab with domain filter
    const params = new URLSearchParams()
    params.set('section', 'mailboxes')
    params.set('domains', selectedDomainNames.join(','))
    
    navigate(`/domains-emails?${params.toString()}`)
  }

  const handleSyncInboxKit = async () => {
    setIsSyncingInboxKit(true)
    try {
      const { data, error } = await supabase.functions.invoke('sync-inboxkit-domains', {
        body: {}
      })

      console.log('[INBOXKIT-SYNC] Function response:', { data, error })

      if (error) {
        console.error('[INBOXKIT-SYNC] Function returned error:', JSON.stringify(error, null, 2))
        let errorMessage = error.message || 'Failed to sync domains from InboxKit'
        if (error.context) {
          if (error.context instanceof Response) {
            try {
              const errorBody = await error.context.clone().json()
              errorMessage = errorBody.error || errorMessage
            } catch {
              try {
                const errorText = await error.context.clone().text()
                errorMessage = errorText || errorMessage
              } catch {}
            }
          } else if (typeof error.context === 'object' && error.context.error) {
            errorMessage = error.context.error
          }
        }
        toast({
          title: "InboxKit Sync Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 15000
        })
        return
      }

      if (data && data.success) {
        toast({
          title: "InboxKit Sync Complete",
          description: `Successfully synced ${data.synced} domain${data.synced !== 1 ? 's' : ''} from InboxKit${data.errors > 0 ? ` (${data.errors} errors)` : ''}`,
        })
        queryClient.invalidateQueries({ queryKey: ['domains'] })
        if (actualRefetch) {
          await actualRefetch()
        }
      } else {
        const errorMessage = data?.error || 'InboxKit sync failed'
        toast({
          title: "InboxKit Sync Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 15000
        })
      }
    } catch (error: any) {
      console.error('[INBOXKIT-SYNC] Exception:', error)
      toast({
        title: "InboxKit Sync Failed",
        description: error.message || 'Failed to sync domains from InboxKit',
        variant: "destructive",
        duration: 15000
      })
    } finally {
      setIsSyncingInboxKit(false)
    }
  }

  const handleSyncSpaceship = async () => {
    setIsSyncingSpaceship(true)
    try {
      const { data, error } = await supabase.functions.invoke('sync-spaceship-domains', {
        body: {}
      })
      
      console.log('[SYNC] Function response:', { data, error })
      
      // If error object exists, try to extract detailed error message
      if (error) {
        console.error('[SYNC] Function returned error:', JSON.stringify(error, null, 2))
        
        // Try to extract error from response
        let errorMessage = error.message || 'Failed to sync domains from Spaceship'
        
        // Check if error has context (Response object)
        if (error.context) {
          console.log('[SYNC] Error context type:', typeof error.context, error.context)
          if (error.context instanceof Response) {
            try {
              const errorBody = await error.context.clone().json()
              console.error('[SYNC] Parsed error response body:', JSON.stringify(errorBody, null, 2))
              errorMessage = errorBody.error || errorMessage
            } catch (parseError) {
              try {
                const errorText = await error.context.clone().text()
                console.error('[SYNC] Error response text:', errorText)
                errorMessage = errorText || errorMessage
              } catch (textError) {
                console.error('[SYNC] Could not read error response:', textError)
              }
            }
          } else if (typeof error.context === 'object') {
            console.error('[SYNC] Error context (non-Response):', JSON.stringify(error.context, null, 2))
            if (error.context.error) {
              errorMessage = error.context.error
            }
          }
        }
        
        toast({
          title: "Sync Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 15000 // Show longer for detailed errors
        })
        return
      }
      
      if (data && data.success) {
        toast({
          title: "Sync Complete",
          description: `Successfully synced ${data.synced} domain${data.synced !== 1 ? 's' : ''} from Spaceship${data.errors > 0 ? ` (${data.errors} errors)` : ''}`,
        })
        
        // Refresh domains list (invalidate query to force reload)
        queryClient.invalidateQueries({ queryKey: ['domains'] })
        if (actualRefetch) {
          await actualRefetch()
        }
      } else {
        // Extract error message from response
        const errorMessage = data?.error || 'Sync failed'
        console.error('[SYNC] Sync failed - response data:', JSON.stringify(data, null, 2))
        toast({
          title: "Sync Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 15000
        })
      }
    } catch (error: any) {
      console.error('[SYNC] Exception caught:', JSON.stringify(error, null, 2))
      console.error('[SYNC] Error stack:', error.stack)
      
      const errorMessage = error.message || error.error || 'Failed to sync domains from Spaceship'
      toast({
        title: "Sync Failed",
        description: errorMessage,
        variant: "destructive",
        duration: 15000
      })
    } finally {
      setIsSyncingSpaceship(false)
    }
  }

  return (
    <div className="w-full space-y-6">

      {/* Compact Filters - Only show if not using filtered props */}
      {!propFilteredDomains && (
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by domain, client, provider..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-9"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Client</label>
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {uniqueClients.map(client => (
                      <SelectItem key={client} value={client}>{client}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 opacity-0">Clear</label>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearchTerm('')
                    setStatusFilter('all')
                    setClientFilter('all')
                  }}
                  className="w-full h-9"
                  size="sm"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Domains Table */}
      <Card className="border-gray-200">
        <CardHeader className="pb-6">
          {/* Top Row - Title and View Selector */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <CardTitle className="text-lg">
                Domains ({filteredDomains.length})
                {selectedDomains.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-blue-600">
                    ({selectedDomains.length} selected)
                  </span>
                )}
              </CardTitle>
              <ViewSelector 
                onViewChange={(view) => {
                  // Apply view settings when changed
                  setSearchTerm(view.searchQuery)
                  setStatusFilter(view.statusFilter)
                  setClientFilter(view.clientFilter)
                  setVisibleColumns(view.visibleColumns)
                  if (onSearchChange) onSearchChange(view.searchQuery)
                  if (onFiltersChange) onFiltersChange(view.filters)
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page:</span>
              <Select 
                value={itemsPerPage.toString()} 
                onValueChange={(value) => {
                  setItemsPerPage(Number(value))
                  setCurrentPage(1)
                }}
              >
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                 </SelectContent>
               </Select>
             </div>
          </div>

          {/* Bottom Row - Actions and Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {selectedDomains.length > 0 && (
                <Button 
                  onClick={() => setShowBulkEditDialog(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Bulk Update ({selectedDomains.length})
                </Button>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search by domain, client, provider..." 
                  value={onSearchChange ? searchQuery : searchTerm} 
                  onChange={e => onSearchChange ? onSearchChange(e.target.value) : setSearchTerm(e.target.value)} 
                  className="pl-10 w-80" 
                />
              </div>
              {propFilteredDomains && filterFields && filterFields.length > 0 && (
                <AdvancedFilter 
                  fields={filterFields} 
                  filters={filters} 
                  onFiltersChange={onFiltersChange || (() => {})} 
                  onApply={() => {}} 
                  onClear={() => onFiltersChange ? onFiltersChange([]) : {}} 
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={handleExport}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              
              <Button
                onClick={handleSyncSpaceship} 
                variant="outline" 
                size="sm"
                disabled={isSyncingSpaceship}
              >
                {isSyncingSpaceship ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Cloud className="w-4 h-4 mr-2" />
                    Sync Spaceship
                  </>
                )}
              </Button>

              <Button
                onClick={handleSyncInboxKit}
                variant="outline"
                size="sm"
                disabled={isSyncingInboxKit}
              >
                {isSyncingInboxKit ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Cloud className="w-4 h-4 mr-2" />
                    Sync InboxKit
                  </>
                )}
              </Button>

              <Button variant="outline" size="sm" onClick={() => setShowColumnManager(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Manage Columns
              </Button>

              {selectedDomains.length > 0 && (
                <Button 
                  onClick={handleSeeMailboxes}
                  size="sm"
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  See mailboxes ({selectedDomains.length})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {actualIsLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading domains...</div>
          ) : filteredDomains.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="mx-auto h-12 w-12 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No domains found</h3>
              <p className="text-sm text-gray-500">
                {actualDomains.length === 0 ? 'Upload domains to get started' : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border mx-4 mb-4 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                   <TableHeader>
                     <TableRow className="bg-muted/50">
                        <TableHead className="w-12">
                          <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={handleSelectAll}
                            aria-label="Select all domains"
                          />
                        </TableHead>
                         {visibleColumns.includes('domain') && (
                            <TableColumnContextMenu
                              columnId="domain"
                              columnLabel={columnLabels.domain}
                              isPinned={pinnedColumns.includes('domain')}
                              isVisible={visibleColumns.includes('domain')}
                              currentSort={sortConfig}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.domain}
                              </TableHead>
                            </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('client_name') && (
                            <TableColumnContextMenu
                              columnId="client_name"
                              columnLabel={columnLabels.client_name || 'Client'}
                              isPinned={pinnedColumns.includes('client_name')}
                              isVisible={visibleColumns.includes('client_name')}
                              currentSort={sortConfig}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.client_name || 'Client'}
                              </TableHead>
                            </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('status') && (
                            <TableColumnContextMenu
                              columnId="status"
                              columnLabel={columnLabels.status}
                              isPinned={pinnedColumns.includes('status')}
                              isVisible={visibleColumns.includes('status')}
                              currentSort={sortConfig}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.status}
                              </TableHead>
                            </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('domain_registrar') && (
                            <TableColumnContextMenu
                              columnId="domain_registrar"
                              columnLabel={columnLabels.domain_registrar}
                              isPinned={pinnedColumns.includes('domain_registrar')}
                              isVisible={visibleColumns.includes('domain_registrar')}
                              currentSort={sortConfig}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.domain_registrar}
                              </TableHead>
                            </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('mailbox_provider') && (
                            <TableColumnContextMenu
                              columnId="mailbox_provider"
                              columnLabel={columnLabels.mailbox_provider}
                              isPinned={pinnedColumns.includes('mailbox_provider')}
                              isVisible={visibleColumns.includes('mailbox_provider')}
                              currentSort={sortConfig}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.mailbox_provider}
                              </TableHead>
                            </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('email_provider') && (
                            <TableColumnContextMenu
                              columnId="email_provider"
                              columnLabel={columnLabels.email_provider}
                              isPinned={pinnedColumns.includes('email_provider')}
                              isVisible={visibleColumns.includes('email_provider')}
                              currentSort={sortConfig}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.email_provider}
                              </TableHead>
                            </TableColumnContextMenu>
                         )}
                         {(visibleColumns.includes('total_mailboxes') || visibleColumns.includes('active_mailboxes')) && (
                           <TableHead className="font-semibold text-xs">
                             <div>Mailboxes</div>
                             <div className="text-[10px] font-normal text-muted-foreground">Active / Total</div>
                           </TableHead>
                         )}
                         {visibleColumns.includes('total_emails_sent') && (
                            <TableColumnContextMenu
                              columnId="total_emails_sent"
                              columnLabel={columnLabels.total_emails_sent || 'Emails Sent'}
                              isPinned={pinnedColumns.includes('total_emails_sent')}
                              isVisible={visibleColumns.includes('total_emails_sent')}
                              currentSort={sortConfig}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.total_emails_sent || 'Emails Sent'}
                              </TableHead>
                            </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('total_replies') && (
                           <TableColumnContextMenu
                             columnId="total_replies"
                             columnLabel={columnLabels.total_replies || 'Replies'}
                             isPinned={pinnedColumns.includes('total_replies')}
                             isVisible={visibleColumns.includes('total_replies')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.total_replies || 'Replies'}
                             </TableHead>
                           </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('total_bounces') && (
                           <TableColumnContextMenu
                             columnId="total_bounces"
                             columnLabel={columnLabels.total_bounces || 'Bounces'}
                             isPinned={pinnedColumns.includes('total_bounces')}
                             isVisible={visibleColumns.includes('total_bounces')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.total_bounces || 'Bounces'}
                             </TableHead>
                           </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('reply_rate') && (
                           <TableColumnContextMenu
                             columnId="reply_rate"
                             columnLabel={columnLabels.reply_rate || 'Reply %'}
                             isPinned={pinnedColumns.includes('reply_rate')}
                             isVisible={visibleColumns.includes('reply_rate')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.reply_rate || 'Reply %'}
                             </TableHead>
                           </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('bounce_rate') && (
                           <TableColumnContextMenu
                             columnId="bounce_rate"
                             columnLabel={columnLabels.bounce_rate || 'Bounce %'}
                             isPinned={pinnedColumns.includes('bounce_rate')}
                             isVisible={visibleColumns.includes('bounce_rate')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.bounce_rate || 'Bounce %'}
                             </TableHead>
                           </TableColumnContextMenu>
                          )}
                          {visibleColumns.includes('purchase_date') && (
                            <TableColumnContextMenu
                              columnId="purchase_date"
                              columnLabel={columnLabels.purchase_date || 'Purchase Date'}
                              isPinned={pinnedColumns.includes('purchase_date')}
                              isVisible={visibleColumns.includes('purchase_date')}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.purchase_date || 'Purchase Date'}
                              </TableHead>
                            </TableColumnContextMenu>
                          )}
                         {visibleColumns.includes('price') && (
                            <TableColumnContextMenu
                              columnId="price"
                              columnLabel={columnLabels.price || 'Price'}
                              isPinned={pinnedColumns.includes('price')}
                              isVisible={visibleColumns.includes('price')}
                              onPin={handlePinColumn}
                              onUnpin={handleUnpinColumn}
                              onHide={handleHideColumn}
                              onShow={handleShowColumn}
                              onRename={handleRenameColumn}
                              onFilter={handleFilterColumn}
                              onSort={handleSort}
                            >
                              <TableHead className="font-semibold text-xs cursor-pointer">
                                {columnLabels.price || 'Price'}
                              </TableHead>
                            </TableColumnContextMenu>
                          )}
                         {visibleColumns.includes('auto_renewal') && (
                           <TableColumnContextMenu
                             columnId="auto_renewal"
                             columnLabel={columnLabels.auto_renewal || 'Auto Renewal'}
                             isPinned={pinnedColumns.includes('auto_renewal')}
                             isVisible={visibleColumns.includes('auto_renewal')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.auto_renewal || 'Auto Renewal'}
                             </TableHead>
                           </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('renewal_date') && (
                           <TableColumnContextMenu
                             columnId="renewal_date"
                             columnLabel={columnLabels.renewal_date || 'Renewal Date'}
                             isPinned={pinnedColumns.includes('renewal_date')}
                             isVisible={visibleColumns.includes('renewal_date')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.renewal_date || 'Renewal Date'}
                             </TableHead>
                           </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('expiry_date') && (
                           <TableColumnContextMenu
                             columnId="expiry_date"
                             columnLabel={columnLabels.expiry_date || 'Expiry Date'}
                             isPinned={pinnedColumns.includes('expiry_date')}
                             isVisible={visibleColumns.includes('expiry_date')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.expiry_date || 'Expiry Date'}
                             </TableHead>
                           </TableColumnContextMenu>
                         )}
                         {visibleColumns.includes('notes') && (
                           <TableColumnContextMenu
                             columnId="notes"
                             columnLabel={columnLabels.notes || 'Notes'}
                             isPinned={pinnedColumns.includes('notes')}
                             isVisible={visibleColumns.includes('notes')}
                             onPin={handlePinColumn}
                             onUnpin={handleUnpinColumn}
                             onHide={handleHideColumn}
                             onShow={handleShowColumn}
                             onRename={handleRenameColumn}
                             onFilter={handleFilterColumn}
                           >
                             <TableHead className="font-semibold text-xs cursor-pointer">
                               {columnLabels.notes || 'Notes'}
                             </TableHead>
                           </TableColumnContextMenu>
                         )}
                         <TableHead className="font-semibold text-xs w-20">Actions</TableHead>
                      </TableRow>
                  </TableHeader>
                  <TableBody>
                     {currentDomains.map((domain) => (
                       <TableRow key={domain.id} className="hover:bg-muted/30">
                         <TableCell className="py-3">
                           <Checkbox
                             checked={selectedDomains.includes(domain.id)}
                             onCheckedChange={(checked) => handleSelectDomain(domain.id, checked)}
                             aria-label={`Select ${domain.domain}`}
                           />
                         </TableCell>
                         
                         {visibleColumns.includes('domain') && (
                           <TableCell className="py-3">
                             <div className="font-medium text-sm">{domain.domain}</div>
                           </TableCell>
                         )}

                        {visibleColumns.includes('client_name') && (
                          <TableCell className="py-3">
                            <span className="text-sm">{domain.client_name || '-'}</span>
                          </TableCell>
                        )}
                         
                         {visibleColumns.includes('status') && (
                           <TableCell className="py-3">
                             <Badge 
                               variant={domain.status === 'Active' ? 'default' : 'secondary'}
                               className={`text-xs ${
                                 domain.status === 'Active' 
                                   ? 'bg-green-100 text-green-800 border-green-200' 
                                   : domain.status === 'Expired'
                                   ? 'bg-red-100 text-red-800 border-red-200'
                                   : 'bg-gray-100 text-gray-800 border-gray-200'
                               }`}
                             >
                               {domain.status 
                                 ? domain.status.charAt(0).toUpperCase() + domain.status.slice(1).toLowerCase()
                                 : 'Unknown'
                               }
                             </Badge>
                           </TableCell>
                         )}

                         {visibleColumns.includes('domain_registrar') && (
                           <TableCell className="py-3">
                             <span className="text-sm">{domain.domain_registrar || '-'}</span>
                           </TableCell>
                         )}

                         {visibleColumns.includes('mailbox_provider') && (
                           <TableCell className="py-3">
                             <span className="text-sm">{domain.mailbox_provider || '-'}</span>
                           </TableCell>
                         )}

                         {visibleColumns.includes('email_provider') && (
                           <TableCell className="py-3">
                             <span className="text-sm">{domain.email_provider || '-'}</span>
                           </TableCell>
                         )}

                         {(visibleColumns.includes('total_mailboxes') || visibleColumns.includes('active_mailboxes')) && (
                           <TableCell className="py-3">
                             <span className="text-sm font-medium">
                               {domain.active_mailboxes || 0} / {domain.total_mailboxes || 0}
                             </span>
                           </TableCell>
                         )}

                         {visibleColumns.includes('total_emails_sent') && (
                           <TableCell className="py-3">
                             <span className="text-sm font-medium">
                               {domain.total_emails_sent?.toLocaleString() || '0'}
                             </span>
                           </TableCell>
                         )}

                         {visibleColumns.includes('total_replies') && (
                           <TableCell className="py-3">
                             <span className="text-sm font-medium">
                               {domain.total_email_replies?.toLocaleString() || '0'}
                               <span className="text-muted-foreground font-normal ml-1">
                                 ({domain.total_emails_sent > 0
                                   ? `${((domain.total_email_replies / domain.total_emails_sent) * 100).toFixed(1)}%`
                                   : '0.0%'})
                               </span>
                             </span>
                           </TableCell>
                         )}

                         {visibleColumns.includes('total_bounces') && (
                           <TableCell className="py-3">
                             <span className="text-sm font-medium">
                               {domain.total_email_bounces?.toLocaleString() || '0'}
                               <span className="text-muted-foreground font-normal ml-1">
                                 ({domain.total_emails_sent > 0
                                   ? `${((domain.total_email_bounces / domain.total_emails_sent) * 100).toFixed(1)}%`
                                   : '0.0%'})
                               </span>
                             </span>
                           </TableCell>
                          )}

                          {visibleColumns.includes('purchase_date') && (
                            <TableCell className="py-3">
                              <span className="text-sm">{formatDateDisplay(domain.purchase_date)}</span>
                            </TableCell>
                          )}

                          {visibleColumns.includes('price') && (
                            <TableCell className="py-3">
                              {domain.price ? (
                                <span className="text-sm font-medium">${Number(domain.price).toFixed(2)}</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}

                         {visibleColumns.includes('auto_renewal') && (
                           <TableCell className="py-3">
                             <Badge 
                               variant={domain.auto_renewal ? 'default' : 'secondary'}
                               className={`text-xs ${
                                 domain.auto_renewal 
                                   ? 'bg-blue-100 text-blue-800 border-blue-200' 
                                   : 'bg-gray-100 text-gray-800 border-gray-200'
                               }`}
                             >
                               {domain.auto_renewal ? 'Yes' : 'No'}
                             </Badge>
                           </TableCell>
                         )}

                         {visibleColumns.includes('renewal_date') && (
                           <TableCell className="py-3">
                             <span className="text-sm">{formatDateDisplay(domain.renewal_date)}</span>
                           </TableCell>
                         )}

                         {visibleColumns.includes('expiry_date') && (
                           <TableCell className="py-3">
                             <span className="text-sm">{formatDateDisplay(domain.expiry_date)}</span>
                           </TableCell>
                         )}

                         {visibleColumns.includes('notes') && (
                           <TableCell className="py-3 text-sm text-muted-foreground max-w-[200px]">
                             {domain.notes ? (
                               <span 
                                 className="cursor-pointer hover:text-foreground transition-colors truncate block" 
                                 title={domain.notes}
                               >
                                 {domain.notes.length > 30 ? 
                                   `${domain.notes.substring(0, 30)}...` : 
                                   domain.notes
                                 }
                               </span>
                             ) : '-'}
                           </TableCell>
                         )}

                         <TableCell className="py-3">
                           <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <Edit className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                               <DropdownMenuItem onClick={() => handleEditDomain(domain)}>
                                 <Edit className="mr-2 h-4 w-4" />
                                 Edit
                               </DropdownMenuItem>
                               <DropdownMenuItem 
                                 onClick={() => handleDeleteDomain(domain)}
                                 className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          
          {/* Pagination */}
          {filteredDomains.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t bg-gray-50/50">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredDomains.length)} of {filteredDomains.length} domains
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {(() => {
                    const maxVisible = 7; // Show up to 7 page numbers
                    const delta = Math.floor(maxVisible / 2);
                    let start = Math.max(1, currentPage - delta);
                    let end = Math.min(totalPages, currentPage + delta);
                    
                    // Adjust if we're near the beginning or end
                    if (end - start + 1 < maxVisible) {
                      if (start === 1) {
                        end = Math.min(totalPages, start + maxVisible - 1);
                      } else if (end === totalPages) {
                        start = Math.max(1, end - maxVisible + 1);
                      }
                    }
                    
                    const pages = [];
                    
                    // Add first page and ellipsis if needed
                    if (start > 1) {
                      pages.push(
                        <Button
                          key={1}
                          variant={currentPage === 1 ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          className="w-8 h-8 p-0"
                        >
                          1
                        </Button>
                      );
                      if (start > 2) {
                        pages.push(<span key="ellipsis1" className="text-muted-foreground px-1">...</span>);
                      }
                    }
                    
                    // Add visible page range
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button
                          key={i}
                          variant={currentPage === i ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(i)}
                          className="w-8 h-8 p-0"
                        >
                          {i}
                        </Button>
                      );
                    }
                    
                    // Add ellipsis and last page if needed
                    if (end < totalPages) {
                      if (end < totalPages - 1) {
                        pages.push(<span key="ellipsis2" className="text-muted-foreground px-1">...</span>);
                      }
                      pages.push(
                        <Button
                          key={totalPages}
                          variant={currentPage === totalPages ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                          className="w-8 h-8 p-0"
                        >
                          {totalPages}
                        </Button>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Single Domain Edit Dialog */}
      <DomainBulkEditDialog
        open={showSingleEditDialog}
        onOpenChange={(open) => {
          setShowSingleEditDialog(open)
          if (!open) setEditingDomain(null)
        }}
        selectedDomains={editingDomain ? [editingDomain.id] : []}
        selectedCount={1}
        onUpdate={actualRefetch}
        onClearSelection={() => setEditingDomain(null)}
        singleDomain={editingDomain}
        isSingleEdit={true}
        statusOptions={statusOptionsList}
        registrarOptions={registrarOptionsList}
        mailboxProviderOptions={mailboxProviderOptionsList}
        emailProviderOptions={emailProviderOptionsList}
      />

      {/* Upload Dialog */}
      <DomainUploadDialog 
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onUploadComplete={() => {
          setShowUploadDialog(false)
          actualRefetch()
        }}
      />

      {/* Bulk Edit Dialog */}
      <DomainBulkEditDialog
        open={showBulkEditDialog}
        onOpenChange={setShowBulkEditDialog}
        selectedDomains={selectedDomains}
        selectedCount={selectedDomains.length}
        onUpdate={actualRefetch}
        onClearSelection={() => setSelectedDomains([])}
        statusOptions={statusOptionsList}
        registrarOptions={registrarOptionsList}
        mailboxProviderOptions={mailboxProviderOptionsList}
        emailProviderOptions={emailProviderOptionsList}
      />

      {/* Column Manager Dialog */}
      <DomainColumnManagerDialog
        open={showColumnManager}
        onOpenChange={setShowColumnManager}
        onColumnsUpdated={(columns) => {
          // Update visible columns based on the column configuration
          const visibleColumnIds = columns
            .filter(col => col.visible)
            .map(col => col.id)
          setVisibleColumns(visibleColumnIds)
          
          // Update current view to persist column changes
          updateView(currentViewId, { visibleColumns: visibleColumnIds })
         }}
       />

       {/* Add Domain Dialog */}
       <DomainAddDialog
         open={showAddDomainDialog}
         onOpenChange={setShowAddDomainDialog}
         onDomainAdded={actualRefetch}
       />
    </div>
  )
}
